import React, { useState, useEffect } from 'react';
import { Project, ProjectCategory } from '../types';
import { ProjectCard } from '../components/ProjectCard';
import { ProjectModal } from '../components/ProjectModal';
import { SearchModal } from '../components/SearchModal';
import { TopProjectsSlideshow } from '../components/TopProjectsSlideshow';
import { Sidebar } from '../components/Sidebar';
import { Filter, ArrowUpDown, Calendar, Search, Sparkles, Menu, Youtube, Github, RefreshCw, AlertTriangle, LayoutGrid, List, Moon, Sun } from 'lucide-react';

const CATEGORIES: ('All' | ProjectCategory)[] = ['All', 'Web App', 'Frontend', 'Full-Stack', '3D/Creative', 'Utility'];

interface HomePageProps {
  projects: Project[];
  lastUpdated?: number | null;
  isDark?: boolean;
  toggleTheme?: () => void;
}

export const HomePage: React.FC<HomePageProps> = ({ projects, lastUpdated, isDark, toggleTheme }) => {
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<'All' | ProjectCategory>('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  
  // New: View Mode State
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Live Clock State
  const [currentTime, setCurrentTime] = useState(new Date());

  // Calculate current day (max ID) for Sidebar
  const maxDay = projects.length > 0 
     ? Math.max(...projects.map(p => {
         const num = parseInt(p.id);
         return isNaN(num) ? 0 : num;
     }))
     : 0;

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Filter and Sort Projects
  useEffect(() => {
    let result = selectedCategory === 'All'
      ? projects
      : projects.filter(p => p.category === selectedCategory);

    // Sort
    result = [...result].sort((a, b) => {
      if (sortOrder === 'newest') {
        return b.createdAt - a.createdAt;
      } else {
        return a.createdAt - b.createdAt;
      }
    });

    setFilteredProjects(result);
  }, [selectedCategory, projects, sortOrder]);

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
    setIsSearchOpen(false); // Close search if opening from there
  };

  const toggleSort = () => {
    setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest');
  };

  const toggleViewMode = () => {
    setViewMode(prev => prev === 'grid' ? 'list' : 'grid');
  };

  return (
    <div className="flex flex-col min-h-screen">
        
        {/* Theme Toggle Button (Specific to HomePage) - Hidden when modal is open */}
        {toggleTheme && !isModalOpen && (
            <button
            onClick={toggleTheme}
            className="fixed top-5 right-5 z-[90] p-2.5 rounded-full backdrop-blur-md transition-all duration-300 shadow-lg group
                bg-white/60 border border-white/40 text-yellow-600 hover:bg-white/80 hover:scale-110
                dark:bg-white/10 dark:border-white/10 dark:text-slate-300 dark:hover:text-white dark:hover:bg-white/20"
            aria-label="Toggle Theme"
            >
            {isDark ? (
                <Moon size={24} className="fill-current" />
            ) : (
                <Sun size={24} className="fill-current" />
            )}
            </button>
        )}

        {/* Floating Menu Button - Always Visible but hidden when modal is open */}
        {!isModalOpen && (
          <button 
            onClick={() => setIsSidebarOpen(true)}
            className="fixed top-5 left-5 z-50 p-2.5 rounded-full backdrop-blur-md transition-all duration-300 shadow-lg hover:scale-110
              bg-white/60 border border-white/40 text-slate-700 hover:bg-white/80
              dark:bg-white/10 dark:border-white/10 dark:text-slate-300 dark:hover:text-white dark:hover:bg-white/20"
            aria-label="Open Menu"
          >
            <Menu size={24} />
          </button>
        )}

        {/* Header */}
        <header className="relative z-20 pt-12">
          <div className="container mx-auto px-4 flex flex-col items-center justify-center text-center pb-6">
              <div className="flex flex-col items-center animate-slide-up">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={16} className="text-yellow-500 dark:text-yellow-400 fill-current" />
                    <span className="text-xs font-bold text-yellow-600 dark:text-yellow-400 uppercase tracking-widest">Challenge in Progress</span>
                    <Sparkles size={16} className="text-yellow-500 dark:text-yellow-400 fill-current" />
                  </div>
                  
                  <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white mb-2 tracking-tighter uppercase italic drop-shadow-sm">
                    The <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">365 Day</span> Journey
                  </h1>
                  
                  <p className="text-sm text-slate-600 dark:text-slate-400 font-light max-w-xl mx-auto leading-relaxed">
                    Building one web application every single day. <strong className="text-slate-900 dark:text-white font-medium">1 Year. No Excuses.</strong>
                  </p>
              </div>

              {/* Compact Filters Row */}
              <div className="flex flex-wrap justify-center gap-1.5 mt-6 w-full max-w-3xl">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide transition-all border ${
                      selectedCategory === cat
                        ? 'bg-white dark:bg-white text-primary dark:text-black border-primary/20 shadow-lg scale-105'
                        : 'bg-white/40 dark:bg-white/5 border-white/40 dark:border-white/5 text-slate-600 dark:text-slate-400 hover:bg-white/60 dark:hover:bg-white/10 hover:text-primary dark:hover:text-white'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
          </div>
        </header>

        {/* Top 10 Slideshow (Infinite Marquee) */}
        <section className="relative z-10 animate-fade-in">
          <TopProjectsSlideshow projects={projects} onSelect={handleProjectClick} />
        </section>

        {/* Main Grid */}
        <main className="relative z-10 container mx-auto px-4 md:px-6 pb-24 flex-grow">
          
          {/* Controls Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-center max-w-[1400px] mx-auto mb-6 px-1 gap-4">
            <div className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-3 flex-1">
               <span className="font-semibold text-slate-700 dark:text-slate-300">Daily Uploads: {filteredProjects.length}</span>
               <span className="w-1 h-1 rounded-full bg-slate-400 dark:bg-slate-700 hidden sm:block"></span>
               <span className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded bg-white/40 dark:bg-slate-800/40 border border-slate-200 dark:border-white/5">
                 <Filter size={10} /> {selectedCategory}
               </span>
            </div>

            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={() => setIsSearchOpen(true)}
                className="glass-panel flex-1 sm:flex-none justify-center flex items-center gap-2 text-[11px] font-medium text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-white px-4 py-2 rounded-lg transition-all group"
              >
                <Search size={12} className="group-hover:text-primary transition-colors" />
                <span>Search</span>
                <span className="ml-1 px-1.5 py-0.5 bg-black/5 dark:bg-black/20 rounded border border-black/5 dark:border-white/5 text-[9px] hidden sm:inline-block font-mono">/</span>
              </button>

              {/* Toggle List/Grid View */}
              <button
                onClick={toggleViewMode}
                className="glass-panel flex-none justify-center flex items-center gap-2 text-[11px] font-medium text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-white px-3 py-2 rounded-lg transition-all min-w-[40px]"
                title={viewMode === 'grid' ? "Switch to List View" : "Switch to Grid View"}
              >
                {viewMode === 'grid' ? <List size={14} /> : <LayoutGrid size={14} />}
              </button>

              <button 
                onClick={toggleSort}
                className="glass-panel flex-1 sm:flex-none justify-center flex items-center gap-2 text-[11px] font-medium text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-white px-4 py-2 rounded-lg transition-all"
              >
                <Calendar size={12} />
                <span className="">{sortOrder === 'newest' ? 'Newest' : 'Oldest'}</span>
                <ArrowUpDown size={12} />
              </button>
            </div>
          </div>

          {/* 
            GRID SYSTEM UPDATED - 2 Cards Mobile, Adaptive Desktop
          */}
          <div className={`
             grid gap-3 md:gap-6 mx-auto transition-all duration-300
             ${viewMode === 'grid' 
                ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 max-w-[1400px]'  // 2 cols on mobile, 3 on tablet, 4 on desktop
                : 'grid-cols-1 max-w-2xl'
             }
          `}>
            {filteredProjects.map((project) => (
              <ProjectCard 
                key={project.id} 
                project={project} 
                onClick={handleProjectClick} 
              />
            ))}
          </div>

          {filteredProjects.length === 0 && (
             <div className="text-center py-32 border border-dashed border-slate-300 dark:border-white/10 rounded-xl bg-white/20 dark:bg-white/5 max-w-6xl mx-auto col-span-full">
               <p className="text-slate-500 dark:text-slate-400 text-sm">No projects found in this category.</p>
               <button onClick={() => setSelectedCategory('All')} className="mt-2 text-primary text-xs hover:underline">View All</button>
             </div>
          )}
        </main>

        {/* Clean Footer - Strictly Horizontal */}
        <footer className="relative z-10 py-6 border-t border-slate-200/50 dark:border-white/5 bg-white/30 dark:bg-black/40 backdrop-blur-md mt-auto">
           <div className="container mx-auto px-2 flex justify-center">
              <div className="glass-panel flex flex-row items-center gap-3 md:gap-6 text-[10px] md:text-xs text-slate-600 dark:text-slate-500 px-4 md:px-8 py-2 md:py-3 rounded-full shadow-xl whitespace-nowrap overflow-x-auto scrollbar-hide max-w-full">
                  
                  {/* Left: Copyright */}
                  <span className="font-medium text-slate-500 dark:text-slate-400 tracking-wide shrink-0">&copy; IQODEX</span>

                  {/* Divider */}
                  <span className="w-px h-3 bg-slate-300 dark:bg-white/10 shrink-0"></span>

                  {/* Center: Socials */}
                  <div className="flex items-center gap-3 md:gap-5 shrink-0">
                      <a href="https://www.tiktok.com/@my365journeycode" target="_blank" rel="noopener noreferrer" className="text-slate-500 dark:text-slate-400 hover:text-pink-500 dark:hover:text-white transition-all transform hover:scale-110">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
                      </a>
                      <a href="https://www.youtube.com/@365Jurney" target="_blank" rel="noopener noreferrer" className="text-slate-500 dark:text-slate-400 hover:text-red-500 transition-all transform hover:scale-110">
                          <Youtube size={18} />
                      </a>
                      <a href="https://github.com/2026coder" target="_blank" rel="noopener noreferrer" className="text-slate-500 dark:text-slate-400 hover:text-black dark:hover:text-white transition-all transform hover:scale-110">
                          <Github size={18} />
                      </a>
                  </div>

                  {/* Divider */}
                  <span className="w-px h-3 bg-slate-300 dark:bg-white/10 shrink-0"></span>

                  {/* Right: Date & Time & Refresh */}
                  <div className="flex items-center gap-4 shrink-0">
                      {lastUpdated ? (
                          <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                             <RefreshCw size={10} />
                             <span className="text-[9px]">Live Sync Active</span>
                          </div>
                      ) : (
                          <div className="flex items-center gap-1.5 text-yellow-600 dark:text-yellow-500">
                             <AlertTriangle size={10} />
                             <span className="text-[9px]">Sync Pending</span>
                          </div>
                      )}
                      <span className="font-mono font-medium opacity-60 text-slate-500 dark:text-slate-400">
                          {currentTime.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                  </div>

              </div>
           </div>
        </footer>

        {/* Modals & Overlays */}
        <Sidebar 
          isOpen={isSidebarOpen} 
          onClose={() => setIsSidebarOpen(false)} 
          latestDay={maxDay}
        />

        <ProjectModal 
          project={selectedProject} 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
        />

        <SearchModal 
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          projects={projects}
          onSelect={handleProjectClick}
        />

      </div>
  );
};